import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__';
import { IdentityCardStatusEnumType, IdentityCardType } from '__generated__/graphql';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterBar from 'components/FilterBar';
import LoadingIndicator from 'components/LoadingIndicator';

import statusEnumToReadable from 'utils/status-enum-to-readable';

type IdentityCardStats = { numberOfIdentityCards: number; status: string };

const IDENTITY_CARDS_QUERY = gql(`
  query DepartmentIdentityCards(
    $departmentId: ID!
    $limit: Int
    $cursor: ID
    $filters: IdentityCardFilterType
  ) {
    department(id: $departmentId) {
      id
      identityCards(limit: $limit, cursor: $cursor, filters: $filters) {
        nodes {
          id
          cardNumber
          user {
            fullName
          }
          status
          issueDate
          expiryDate
          createdAt
        }
        pageInfo {
          totalCount
          cursor
          hasNextPage
        }
        statistics {
          identityCardStatisticsBreakdownByStatus {
            numberOfIdentityCards
            status
          }
        }
      }
    }
  }
`);

const CREATE_PRINT_SESSION = gql(`
  mutation CreatePrintSession($identityCardIds: [ID!]!, $departmentId: ID!) {
    createPrintSession(identityCardIds: $identityCardIds, departmentId: $departmentId) {
      id
      name
      status
      createdAt
    }
  }
`);

const DepartmentIdentityCards: FC = () => {
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };
  const navigate = useNavigate();

  const { loading, error, data, refetch, fetchMore, variables } = useQuery(IDENTITY_CARDS_QUERY, {
    variables: {
      departmentId,
      limit: 20,
      filters: { status: IdentityCardStatusEnumType.PendingPrinting },
    },
    notifyOnNetworkStatusChange: true,
  });

  const [createPrintSession, { loading: printLoading, error: printError }] =
    useMutation(CREATE_PRINT_SESSION);

  const getStatusFilterWithLabels = (
    statistics: {
      numberOfIdentityCards: number;
      status: string;
    }[]
  ) => {
    const allStatuses = Object.values(IdentityCardStatusEnumType);

    return [
      {
        label: 'All',
        value: undefined,
      },
      ...allStatuses.map(status => {
        const stat = statistics.find(s => s.status === status);
        return {
          label: `${statusEnumToReadable(status).label} (${stat?.numberOfIdentityCards ?? 0})`,
          value: status,
        };
      }),
    ];
  };

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const identityCards = data.department.identityCards.nodes;
  const pageInfo = data.department.identityCards.pageInfo;

  const statistics = data.department.identityCards.statistics;

  return (
    <>
      <FilterBar
        title="Status"
        filters={getStatusFilterWithLabels(
          statistics.identityCardStatisticsBreakdownByStatus as IdentityCardStats[]
        )}
        currentValue={variables?.filters?.status as IdentityCardStatusEnumType}
        onSelect={value => {
          refetch({
            filters: {
              status: value as IdentityCardStatusEnumType,
            },
          });
        }}
      />
      <DataTable
        data={identityCards as IdentityCardType[]}
        onClick={identityCard => navigate(`/identity-cards/${identityCard.id}`)}
        bulkSelectActions={[
          {
            label: 'Print Selected Cards',
            loading: printLoading,
            error: printError,
            action: async selectedIds => {
              createPrintSession({
                variables: {
                  identityCardIds: selectedIds,
                  departmentId,
                },
              }).then(data => navigate(`/print-sessions/${data.data?.createPrintSession.id}`));
            },
          },
        ]}
        columns={[
          {
            label: 'ID',
            fieldName: 'id',
            type: 'SELECT',
          },
          {
            label: 'Card Number',
            fieldName: 'cardNumber',
          },
          {
            label: 'User',
            fieldName: 'user.fullName',
          },
          {
            label: 'Issue Date',
            fieldName: 'issueDate',
            type: 'DATE',
          },
          {
            label: 'Expiry Date',
            fieldName: 'expiryDate',
            type: 'DATE',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage as boolean}
        paginationLoading={loading}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
        totalCount={pageInfo.totalCount as number}
      />
    </>
  );
};

export default DepartmentIdentityCards;
